import { api, fetchServer } from "@/features/utils/fetch-server";
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from "@simplewebauthn/browser";
import type { UsersType, OtherFactor } from "../types";

export class AuthAPI {
  static emailChallenge = async (data: {
    email: string;
    captchaToken?: string;
  }) =>
    api.post<
      {
        success: boolean;
        message?: string;
        otherFactors: OtherFactor[];
      },
      typeof data
    >("/api/users/v1/auth/email/challenge", data);

  static emailVerify = async (data: {
    email: string;
    otp: string;
    captchaToken?: string;
  }): Promise<{
    success: boolean;
    preSignedToken: string;
    otherFactors: OtherFactor[];
    message?: string;
  }> => api.post("/api/users/v1/auth/email/verify", data);

  static addSecondaryEmail = async (data: {
    emailChallengeToken: string;
  }): Promise<{ success: boolean }> =>
    api.post("/api/users/v1/auth/email/add", data);

  static phoneChallenge = async (data: {
    phone: string;
    captchaToken?: string;
  }) =>
    api.post<
      {
        success: boolean;
        message?: string;
        otherFactors: OtherFactor[];
      },
      typeof data
    >("/api/users/v1/auth/phone/challenge", data);

  static phoneVerify = async (data: {
    phone: string;
    otp: string;
    captchaToken?: string;
  }): Promise<{
    success: boolean;
    preSignedToken: string;
    otherFactors: OtherFactor[];
    message?: string;
  }> => api.post("/api/users/v1/auth/phone/verify", data);

  static addSecondaryPhone = async (data: {
    phoneChallengeToken: string;
  }): Promise<{ success: boolean }> =>
    api.post("/api/users/v1/auth/phone/add", data);

  static removeSecondaryEmail = async (data: {
    email: string;
  }): Promise<{ success: boolean }> =>
    api.post("/api/users/v1/auth/email/remove", data);

  static removeSecondaryPhone = async (data: {
    phone: string;
  }): Promise<{ success: boolean }> =>
    api.post("/api/users/v1/auth/phone/remove", data);

  static makePrimaryEmail = async (data: {
    email: string;
  }): Promise<{ success: boolean }> =>
    api.post("/api/users/v1/auth/email/make-primary", data);

  static makePrimaryPhone = async (data: {
    phone: string;
  }): Promise<{ success: boolean }> =>
    api.post("/api/users/v1/auth/phone/make-primary", data);

  static webauthnChallenge = async (data?: {
    userIdOrEmail?: string;
  }): Promise<{
    success: boolean;
    challenge: PublicKeyCredentialRequestOptionsJSON & {
      challengeToken: string;
    };
    message?: string;
  }> => api.post("/api/users/v1/auth/webauthn/challenge", data || {});

  static webauthnVerify = async (data: {
    challengeToken: string;
    credential: AuthenticationResponseJSON;
  }): Promise<{
    success: boolean;
    preSignedToken: string;
    otherFactors: OtherFactor[];
    message?: string;
  }> => api.post("/api/users/v1/auth/webauthn/verify", data);

  static webauthnRegister = async (data: {
    deviceName: string;
  }): Promise<{
    success: boolean;
    challenge: PublicKeyCredentialCreationOptionsJSON & {
      challengeToken: string;
    };
    message?: string;
  }> => api.post("/api/users/v1/auth/webauthn/register", data);

  static webauthnCompleteRegistration = async (data: {
    challengeToken: string;
    credential: RegistrationResponseJSON;
  }): Promise<{ success: boolean; deviceId: string; message?: string }> =>
    api.post("/api/users/v1/auth/webauthn/register/complete", data);

  static webauthnRemoveDevice = async (data: {
    deviceId: string;
  }): Promise<{ success: boolean }> =>
    api.post("/api/users/v1/auth/webauthn/remove", data);

  static login = async (data: {
    preSignedTokens: string[];
  }): Promise<{
    success: boolean;
    token: string;
    user: UsersType;
    message?: string;
  }> => api.post("/api/users/v1/auth/login", data);

  static renewToken = async (data: { token: string }) => {
    const res = await api.post<
      {
        success: boolean;
        token: string;
        user: UsersType;
        message?: string;
      },
      typeof data
    >("/api/users/v1/auth/renew", data);

    if (res.token) {
      localStorage.setItem("auth_token", res.token);
    }

    return res;
  };

  /** Fire-and-forget: the server's reply carries no body worth parsing. */
  static logout = async (): Promise<void> => {
    await fetchServer("/api/users/v1/auth/logout", { method: "POST" });
    return;
  };

  // Password authentication methods
  static passwordCheck = async (data: {
    email: string;
  }): Promise<{
    success: boolean;
    otherFactors?: OtherFactor[];
  }> => api.post("/api/users/v1/auth/factors", data);

  static passwordLogin = async (data: {
    email: string;
    password: string;
  }): Promise<{
    success: boolean;
    preSignedToken: string;
    message?: string;
  }> => api.post("/api/users/v1/auth/password/login", data);

  static passwordVerify = async (data: {
    emailPreSignedToken: string;
    password: string;
  }): Promise<{
    success: boolean;
    preSignedToken: string;
    message?: string;
  }> => api.post("/api/users/v1/auth/password/verify", data);

  static passwordSet = async (data: {
    emailPreSignedToken: string;
    password: string;
  }): Promise<{ success: boolean }> =>
    api.post("/api/users/v1/auth/password/set", data);

  static signup = async (data: {
    emailPreSignedToken: string;
    /** Omitted when the account is created to accept an invitation. */
    companyName?: string;
    phone?: string;
    firstName?: string;
    lastName?: string;
    password: string;
  }): Promise<{
    success: boolean;
    preSignedToken: string;
  }> => api.post("/api/users/v1/auth/signup", data);

  /**
   * Companies that invited this address, for a visitor who has no account yet.
   * The email must be proven — the token the OTP verification returns — so the
   * signup screen can ask the question without it becoming a way to probe
   * addresses.
   */
  static signupInvitations = async (data: {
    emailPreSignedToken: string;
  }): Promise<{
    invitations: {
      client: { id: string; name: string; logo: string | null };
      role: string;
      inviterEmail: string | null;
    }[];
  }> => api.post("/api/users/v1/auth/invitations", data);

  static createApiKey = async (data: {
    name: string;
  }): Promise<{ success: boolean; secret: string }> =>
    api.post("/api/users/v1/api-keys/create", data);

  static removeApiKey = async (data: {
    secret: string;
  }): Promise<{ success: boolean }> =>
    api.post("/api/users/v1/api-keys/remove", data);
}
